import { InterstitialIntroFrame } from '@/api/generated-catch-backend/model';
import { animate, motion, useMotionValue } from 'motion/react';
import { useEffect } from 'react';
import { InterstitialIntro as InterstitialIntroType } from '../../../types';
import { NextButton } from '../next-button/button';
import { InterstitialFooter } from './interstitial-footer';
import {
  BackgroundAsset,
  FRAME_TRANSITION_DURATION,
  getAnimationType,
  getGraphicStyles,
  GraphicContent,
  TextContent,
  useAutoAdvance,
  useFrameNavigation,
} from './interstitial-shared';

type InterstitialIntroProps = {
  interstitial: InterstitialIntroType;
  onNext: () => void;
  onBack?: () => void;
  isBackDisabled?: boolean;
  hideInternalCta?: boolean;
};

const ContentRenderer = ({
  interstitial,
  frame,
  frameIndex,
  animationType,
  onTransitionComplete,
  onNext,
}: {
  interstitial: InterstitialIntroType;
  frame: InterstitialIntroFrame;
  frameIndex: number;
  animationType: { initial: any; animate: any };
  onTransitionComplete?: () => void;
  onNext: () => void;
}) => {
  const graphicStyles = frame.graphic ? getGraphicStyles(frame.graphic) : {};

  // Define card styles based on card type
  const getCardStyles = (cardType: string | null | undefined) => {
    switch (cardType) {
      case 'glass':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '32px',
          opacity: 1.0,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        };
      case 'transparent':
        return {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '16px',
          padding: '32px',
          opacity: 1.0,
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
        };
      case 'none':
      case null:
      case undefined:
      default:
        return null;
    }
  };

  // Testimonial card: headline above, frosted card with graphic + quote
  if (frame.card === 'testimonial') {
    return (
      <motion.div
        key={frameIndex}
        className="interstitial-intro-content"
        initial={animationType.initial}
        animate={animationType.animate}
        transition={{ duration: FRAME_TRANSITION_DURATION, ease: 'easeOut' }}
        style={{ willChange: 'transform, opacity' }}
        {...(onTransitionComplete && { onAnimationComplete: onTransitionComplete })}
      >
        <TextContent frame={frame} textType="above" classNamePrefix="interstitial-intro" />
        <div className="mt-6 flex items-start gap-4 rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md">
          {frame.graphic?.assetUrl && (
            <img
              src={frame.graphic.assetUrl}
              alt=""
              className="h-20 w-20 shrink-0 rounded-lg object-cover"
              draggable={false}
            />
          )}
          <div className="space-y-2">
            <TextContent frame={frame} textType="below" classNamePrefix="interstitial-intro" />
          </div>
        </div>
      </motion.div>
    );
  }

  const content = (
    <>
      <TextContent frame={frame} textType="above" classNamePrefix="interstitial-intro" />
      <div style={graphicStyles}>
        <GraphicContent
          interstitial={interstitial}
          frame={frame}
          classNamePrefix="interstitial-intro"
          onNext={onNext}
        />
      </div>
      <TextContent frame={frame} textType="below" classNamePrefix="interstitial-intro" />
    </>
  );

  const cardStyles = getCardStyles(frame.card as unknown as string);

  if (cardStyles && frame.card !== 'none') {
    return (
      <motion.div
        key={frameIndex}
        className="interstitial-intro-card"
        initial={animationType.initial}
        animate={animationType.animate}
        transition={{
          duration: FRAME_TRANSITION_DURATION,
          ease: 'easeOut',
        }}
        style={{
          willChange: 'transform, opacity',
          ...cardStyles,
        }}
        {...(onTransitionComplete && { onAnimationComplete: onTransitionComplete })}
      >
        {content}
      </motion.div>
    );
  }

  return (
    <motion.div
      key={frameIndex}
      className="interstitial-intro-content"
      initial={animationType.initial}
      animate={animationType.animate}
      transition={{
        duration: FRAME_TRANSITION_DURATION,
        ease: 'easeOut',
      }}
      style={{ willChange: 'transform, opacity' }}
      {...(onTransitionComplete && { onAnimationComplete: onTransitionComplete })}
    >
      {content}
    </motion.div>
  );
};

// Predefined animations that manage their own internal phase transitions.
// When the first frame uses one of these, extra Sanity frames are ignored
// so the React component controls all timing and cross-phase state.
const SELF_MANAGING_ANIMATIONS = new Set(['prequel-risk-stats', 'prequel-social-proof', 'prequel-personalization', 'prequel-risk-map', 'prequel-risk-reduction', 'prequel-screening-plan', 'prequel-bio-age-context', 'prequel-why-catch']);

export const InterstitialIntro = ({ interstitial, onNext, onBack, isBackDisabled = false, hideInternalCta = false }: InterstitialIntroProps) => {
  const allFrames = interstitial.frames;
  const firstAnimation = allFrames[0]?.graphic?.predefinedAnimation;
  const isSelfManaging = allFrames[0]?.graphic?.type === 'predefined' && firstAnimation && SELF_MANAGING_ANIMATIONS.has(firstAnimation);
  const frames = isSelfManaging ? [allFrames[0]] : allFrames;
  const { currentFrameIndex, setCurrentFrameIndex, handleNext, handleBack, currentFrame } = useFrameNavigation(frames, onNext, onBack);
  const progress = useMotionValue(0);

  // Use shared auto-advance logic
  useAutoAdvance(frames, currentFrameIndex, setCurrentFrameIndex);

  // Progress tracking logic (intro-specific)
  useEffect(() => {
    if (frames.length <= 1) {
      animate(progress, 0, { duration: 0.2 });
      return;
    }

    const currentFrame = frames[currentFrameIndex];

    // Check if current frame should auto-advance
    if (!currentFrame.autoAdvance) {
      animate(progress, 0, { duration: 0.2 });
      return;
    }

    // Use explicit fallback for auto_advance_time (default to 3 seconds)
    const advanceTime =
      currentFrame.autoAdvanceTime && currentFrame.autoAdvanceTime > 0 ? currentFrame.autoAdvanceTime : 3;

    let animationControls: ReturnType<typeof animate> | null = null;

    const startProgress = () => {
      animationControls = animate(progress, 1, {
        duration: advanceTime,
        ease: 'linear',
      });
    };

    // Start progress animation
    startProgress();

    return () => {
      if (animationControls) {
        animationControls.stop();
      }
    };
  }, [frames, currentFrameIndex, progress]);

  if (frames.length === 0) {
    return null;
  }

  const animationType = getAnimationType(currentFrame.animateIn);

  return (
    <div className="interstitial-intro-container">
      <div className="interstitial-intro-background" />

      <BackgroundAsset backgroundAsset={currentFrame.backgroundAsset} classNamePrefix="interstitial-intro" />

      <div
        className="interstitial-intro-overlay"
        style={{
          opacity: currentFrame.backgroundOpacity ?? 0.6,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)', // Safari support
        }}
      />

      <div className="interstitial-intro-layout">
        <div className="interstitial-intro-spacer" />

        <ContentRenderer
          interstitial={interstitial}
          frame={currentFrame}
          frameIndex={currentFrameIndex}
          animationType={animationType}
          onNext={handleNext}
        />

        {/* Footer with back button + CTA — frame-level back is always immediate, question-level waits for API */}
        {!hideInternalCta && (
          <InterstitialFooter
            onBack={currentFrameIndex > 0 ? () => setCurrentFrameIndex(currentFrameIndex - 1) : handleBack}
            isBackDisabled={isBackDisabled}
          >
            <NextButton text={currentFrame.ctaText || 'Next'} onNext={handleNext} />
          </InterstitialFooter>
        )}
      </div>
    </div>
  );
};
